import React from 'react';
import { Box } from '@mui/material';
import { useParticipantDashboard } from '../hooks';
import type { ParticipantTab } from '../interfaces';
import {
  ParticipantSearchBar,
  ParticipantTabsNav,
  StatCards,
  WelcomeBanner,
} from './components';
import {
  ApplicationsTab,
  AssessmentsTab,
  InterviewsTab,
  ResumesTab,
  WellnessTab,
} from './containers';

const SEARCH_PLACEHOLDERS: Record<ParticipantTab, string> = {
  applications: 'Search applications...',
  assessments: 'Search assessments...',
  interviews: 'Search interviews...',
  resumes: 'Search...',
  wellness: 'Search wellness...',
};

/**
 * Participant dashboard — thin composition root.
 * Data comes from useParticipantDashboard; each tab is a dedicated
 * container; all visuals are presentational components.
 */
export const ParticipantDashboard = () => {
  const {
    activeTab,
    setActiveTab,
    userAssessments,
    applicationData,
    interviewData,
    wellnessData,
    loading,
    dashLoading,
    wellnessLoading,
    searchTerm,
    setSearchTerm,
    page,
    setPage,
    appTotalCount,
    interviewTotalCount,
    wellnessTotalCount,
    firstName,
    activeApplications,
    pendingAssessments,
    upcomingInterviews,
    getWellnessItems,
  } = useParticipantDashboard();

  const renderActiveTab = () => {
    if (activeTab === 'applications') {
      return (
        <ApplicationsTab
          applications={applicationData}
          totalCount={appTotalCount}
          loading={dashLoading}
          page={page}
          onPageChange={setPage}
        />
      );
    }
    if (activeTab === 'assessments') {
      return <AssessmentsTab assessments={userAssessments} loading={loading} />;
    }
    if (activeTab === 'interviews') {
      return (
        <InterviewsTab
          interviews={interviewData}
          totalCount={interviewTotalCount}
          loading={dashLoading}
          page={page}
          onPageChange={setPage}
        />
      );
    }
    if (activeTab === 'wellness') {
      return (
        <WellnessTab
          invites={wellnessData}
          totalCount={wellnessTotalCount}
          loading={wellnessLoading}
          page={page}
          onPageChange={setPage}
          onRefresh={getWellnessItems}
        />
      );
    }
    return <ResumesTab />;
  };

  return (
    <Box sx={{ position: 'relative', px: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden' }}>
      <WelcomeBanner firstName={firstName} activeApplications={activeApplications} />

      <StatCards
        loading={dashLoading}
        activeApplications={activeApplications}
        pendingAssessments={pendingAssessments}
        upcomingInterviews={upcomingInterviews}
      />

      <ParticipantTabsNav
        activeTab={activeTab}
        onChange={setActiveTab}
        counts={{
          applications: appTotalCount,
          assessments: userAssessments.length,
          interviews: interviewTotalCount,
          wellness: wellnessTotalCount,
        }}
      />

      <ParticipantSearchBar
        value={searchTerm}
        placeholder={SEARCH_PLACEHOLDERS[activeTab]}
        onChange={setSearchTerm}
        onClear={() => setSearchTerm('')}
      />

      <Box
        role="tabpanel"
        id={`participant-tabpanel-${activeTab}`}
        aria-labelledby={`participant-tab-${activeTab}`}
      >
        {renderActiveTab()}
      </Box>
    </Box>
  );
};
